/**
 * Injects styles into <shopify-forms-embed> shadow roots via CSSStyleSheet (CSS can't pierce shadow DOM; :root vars still apply).
 * Textareas use a real border and clear the ::before label backdrop because the scrollbar/resize handle cover inset shadows.
 */

import { extractEmailFromForm, publishFormSubmitted } from 'utils/form-analytics';

const boundForms = new WeakSet<HTMLFormElement>();

const SHOPIFY_FORMS_CSS = `
  :host {
    --forms-base-font-size: 16px !important;
  }

  input:not([type="hidden"], [type="submit"], [type="checkbox"], [type="radio"]),
  textarea,
  button[aria-expanded][aria-controls] {
    font-size: 16px !important;
  }

  input:not([type="hidden"], [type="submit"], [type="checkbox"], [type="radio"]) {
    box-shadow: inset 0 0 0 1px var(--gray-200, #e5e7eb) !important;
    border: none !important;
    border-radius: var(--button-radius, 5px) !important;
    background-color: #ffffff !important;
    color: var(--gray-900, #111827) !important;
    outline: none !important;
    transition: box-shadow 200ms, background-color 200ms !important;
  }

  input:not([type="hidden"], [type="submit"], [type="checkbox"], [type="radio"]):hover {
    background-color: var(--gray-50, #f9fafb) !important;
  }

  input:not([type="hidden"], [type="submit"], [type="checkbox"], [type="radio"]):focus,
  input:not([type="hidden"], [type="submit"], [type="checkbox"], [type="radio"]):focus-visible {
    box-shadow:
      0 0 0 1px color-mix(in srgb, var(--color-blue-600, #2563eb) 50%, transparent),
      inset 0 0 0 1px color-mix(in srgb, var(--color-blue-600, #2563eb) 50%, transparent) !important;
  }

  [class*="_formFieldContainer_"]:has(textarea)::before {
    background-color: transparent !important;
  }

  textarea {
    border: 1px solid var(--gray-200, #e5e7eb) !important;
    box-shadow: none !important;
    border-radius: var(--button-radius, 5px) !important;
    background-color: #ffffff !important;
    color: var(--gray-900, #111827) !important;
    outline: none !important;
    transition: border-color 200ms, background-color 200ms !important;
  }

  textarea:hover {
    background-color: var(--gray-50, #f9fafb) !important;
  }

  textarea:focus,
  textarea:focus-visible {
    border-color: color-mix(in srgb, var(--color-blue-600, #2563eb) 50%, transparent) !important;
    box-shadow: 0 0 0 1px color-mix(in srgb, var(--color-blue-600, #2563eb) 50%, transparent) !important;
  }

  input[type="checkbox"] {
    appearance: none !important;
    -webkit-appearance: none !important;
    border: 1px solid var(--gray-300, #d1d5db) !important;
    border-radius: var(--radius, 4px) !important;
    width: 1.25rem !important;
    height: 1.25rem !important;
    min-width: 1.25rem !important;
    background-color: #ffffff !important;
    background-position: center !important;
    background-repeat: no-repeat !important;
    background-size: calc(1rem * 1) calc(1rem * 1) !important;
    cursor: pointer !important;
    flex-shrink: 0 !important;
    transition: border-color 200ms, background-color 200ms !important;
  }

  input[type="checkbox"]:hover {
    border-color: var(--gray-900, #111827) !important;
  }

  input[type="checkbox"]:checked {
    background-color: var(--gray-900, #111827) !important;
    border-color: var(--gray-900, #111827) !important;
    background-image: url("data:image/svg+xml,%0A%3Csvg xmlns='http://www.w3.org/2000/svg' xml:space='preserve' viewBox='0 0 18 13'%3E%3Cpath fill='none' stroke='%23FFF' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M17 1 6 12 1 7'/%3E%3C/svg%3E") !important;
  }

  button[aria-expanded][aria-controls] {
    box-shadow: inset 0 0 0 1px var(--gray-200, #e5e7eb) !important;
    border: none !important;
    border-radius: var(--button-radius, 5px) !important;
    background-color: #ffffff !important;
    color: var(--gray-900, #111827) !important;
    transition: box-shadow 200ms, background-color 200ms !important;
  }

  button[aria-expanded][aria-controls]:hover {
    background-color: var(--gray-50, #f9fafb) !important;
  }

  button[type="submit"] {
    background-color: var(--gray-900, #111827) !important;
    color: #ffffff !important;
    border: none !important;
    border-radius: var(--button-radius, 5px) !important;
    font-weight: var(--font-weight-medium, 525) !important;
    opacity: 1 !important;
    transition: opacity 200ms ease !important;
  }

  button[type="submit"]:hover:not(:disabled) {
    opacity: 0.8 !important;
  }

  button[type="submit"]:disabled {
    background-color: var(--gray-200, #e5e7eb) !important;
    color: var(--gray-400, #9ca3af) !important;
    opacity: 1 !important;
  }
`;

function injectStyles(embed: Element): void {
  const shadowRoot = (embed as HTMLElement & { shadowRoot: ShadowRoot | null }).shadowRoot;
  if (!shadowRoot) return;

  const sheet = new CSSStyleSheet();
  sheet.replaceSync(SHOPIFY_FORMS_CSS);
  shadowRoot.adoptedStyleSheets = [...shadowRoot.adoptedStyleSheets, sheet];

  bindSubmitTracking(embed, shadowRoot);
}

function bindSubmitTracking(embed: Element, shadowRoot: ShadowRoot): void {
  const form = shadowRoot.querySelector('form');
  if (!(form instanceof HTMLFormElement) || boundForms.has(form)) return;

  boundForms.add(form);

  form.addEventListener('submit', () => {
    const email = extractEmailFromForm(form);
    if (!email) return;

    publishFormSubmitted('shopify_forms', email, embed.getAttribute('form-id') ?? undefined);
  });
}

function initEmbed(embed: Element): void {
  injectStyles(embed);

  const shadowRoot = (embed as HTMLElement & { shadowRoot: ShadowRoot | null }).shadowRoot;
  if (!shadowRoot) return;

  if (shadowRoot.querySelector('form')) return;

  const observer = new MutationObserver(() => {
    bindSubmitTracking(embed, shadowRoot);

    if (shadowRoot.querySelector('form')) {
      observer.disconnect();
    }
  });

  observer.observe(shadowRoot, { childList: true, subtree: true });
}

function initShopifyFormsEmbed(): void {
  document.querySelectorAll('shopify-forms-embed').forEach(initEmbed);

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (node.tagName?.toLowerCase() === 'shopify-forms-embed') {
          initEmbed(node);
        }
        node.querySelectorAll('shopify-forms-embed').forEach(initEmbed);
      }
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initShopifyFormsEmbed);
} else {
  initShopifyFormsEmbed();
}
